// "Remind me later" is a per-login-session choice: the flag lives in
// sessionStorage, scoped by user id so a shared browser cannot hide a
// colleague's update, and is wiped on logout so a fresh login re-shows the
// modal. Every access is guarded — blocked storage degrades to "show the
// modal again", never to a crash.

const DEFERRED_KEY_PREFIX = 'jarvis:patchLogs:deferred:';

/**
 * Same scope rule as the onboarding tours (`getTourUserId`): `authData.id` is
 * the real user id; email is the fallback for sessions without one.
 */
export const getPatchLogUserId = (): string | null => {
  try {
    const raw = window.localStorage.getItem('authData');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { id?: number | string; email?: string };
    const id = parsed.id ?? parsed.email;
    return id === undefined || id === null || id === '' ? null : String(id);
  } catch {
    return null;
  }
};

const deferredKey = (userId: string) => `${DEFERRED_KEY_PREFIX}${userId}`;

export const hasDeferredThisSession = (userId: string | null): boolean => {
  if (!userId) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(deferredKey(userId)) === '1';
  } catch {
    return false;
  }
};

export const markDeferredThisSession = (userId: string | null) => {
  if (!userId) {
    return;
  }
  try {
    window.sessionStorage.setItem(deferredKey(userId), '1');
  } catch {
    // Storage unavailable — the modal simply returns on the next route mount.
  }
};

/** Logout hook: drops every user's deferral flag in this tab. */
export const clearPatchLogDeferralFlags = () => {
  try {
    const storage = window.sessionStorage;
    const stale: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(DEFERRED_KEY_PREFIX)) {
        stale.push(key);
      }
    }
    for (const key of stale) {
      storage.removeItem(key);
    }
  } catch {
    // Nothing to clear.
  }
};
